import {
  Flex,
  Grid,
  GridItem,
  ScaleFade,
  Text,
  useBreakpointValue,
  VStack,
} from '@chakra-ui/react';
import ClassItem from 'components/class/ClassItem';
import ClassItemLoading from 'components/class/ClassItemLoading';
import ClassRecord from 'components/class/classRecords/ClassRecord';
import DeleteClassMd from 'components/class/modals/DeleteClassMd';
import UpdateClassMd from 'components/class/modals/UpdateClassMd';
import Breadcrumbs from 'components/common/Breadcrumbs';
import CourseItem from 'components/course/CourseItem';
import CourseItemLoading from 'components/course/CourseItemLoading';
import UpdateFileSubmissionSessionMd from 'components/subQuest/modals/fileSubmissions/session/UpdateFileSubmissionSessionMd';
import DeleteProgressionMd from 'components/subQuestReport/modals/DeleteProgressionMd';
import DeleteProgressionSessionMd from 'components/subQuestReport/modals/DeleteProgressionSessionMd';
import ProgressionSessionMd from 'components/subQuestReport/modals/ProgressionSessionMd';
import UpdateQuizSessionMd from 'components/subQuestReport/modals/UpdateQuizSessionMd';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { getClassAction } from 'store/modules/class/class-action';
import { publishEntityActions } from 'store/modules/common/publishEntity/publishEntity-slice';
import { courseActions } from 'store/modules/course/course-slice';
import { ReducerStatus } from 'utils/constants';
import { ADMIN, TRAINER } from 'utils/constants/roles';
import {
  handleErrorAndNavigate,
  createErrorInfoFromAction,
} from 'utils/errorHandler';
import { v4 as uuidv4 } from 'uuid';

function ClassAdminPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { accounts } = useMsal();

  // Safe access to user roles with proper null/undefined checks
  const userAccount = accounts?.[0];
  const idTokenClaims = userAccount?.idTokenClaims;
  const roles = idTokenClaims?.roles || [];

  // Check if user has admin or trainer role for admin access
  const hasAdminOrTrainerRole = roles.some((role) =>
    [ADMIN, TRAINER].includes(role)
  );

  /**
   * Permission-based redirect for admin access control
   * If user doesn't have ADMIN or TRAINER role, redirect to non-admin class view
   * Uses replace: true to prevent back navigation to unauthorized page
   */
  useEffect(() => {
    if (!hasAdminOrTrainerRole) {
      navigate(`/classes/${id}`, { replace: true });
    }
  }, [hasAdminOrTrainerRole, navigate, id]);

  // Configuration for ErrorDisplay component - memoized to prevent useEffect dependency changes
  const classAdminEntityConfig = useMemo(
    () => ({
      name: 'Class',
      homeRoute: '/admin/classes',
    }),
    []
  );

  const classObject = useSelector((state) => state.class.class);
  const getClass = useSelector((state) => state.class.getClass);
  const deleteClass = useSelector((state) => state.class.deleteClass);
  const publishStatus = useSelector(
    (state) => state.publishEntity.updatePublishEntity.status
  );

  const columnLayout = useBreakpointValue({
    base: '1fr',
    sm: '1fr 1fr',
    md: '1fr 1fr',
    lg: '1fr 1fr 1fr',
    xl: '1fr 1fr 1fr 1fr',
  });

  const handleCourseItemClick = (courseId) => {
    dispatch(courseActions.setCoursePreview({ modal: true, id: courseId }));
  };

  useEffect(() => {
    dispatch(getClassAction({ id }));
  }, [id, dispatch]);

  useEffect(() => {
    if (classObject) {
      document.title = `Skill Quest | Admin - ${classObject.name}`;
    }
  }, [classObject]);

  useEffect(() => {
    if (deleteClass.status === ReducerStatus.SUCCEEDED) {
      navigate('/admin/classes');
    }
  }, [deleteClass.status, navigate]);

  useEffect(() => {
    if (getClass.status === ReducerStatus.FAILED) {
      // Create standardized error info from Redux action failure
      const errorInfo = createErrorInfoFromAction(
        { payload: getClass.error },
        'Class Not Found',
        'The class you are looking for does not exist or may have been removed.'
      );

      // Handle error and navigate using utility function
      handleErrorAndNavigate(
        dispatch,
        navigate,
        location,
        errorInfo,
        classAdminEntityConfig
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getClass.status,
    getClass.error,
    dispatch,
    navigate,
    classAdminEntityConfig,
  ]);

  // Reset publish status after SUCCESS/FAIL
  useEffect(() => {
    if (
      publishStatus === ReducerStatus.SUCCEEDED ||
      publishStatus === ReducerStatus.FAILED
    ) {
      dispatch(publishEntityActions.resetPublishStatus());
    }
  }, [publishStatus, dispatch]);

  return (
    <Flex ml={{ base: 0, md: 20 }} minH="100vh">
      <DeleteProgressionMd />
      <ProgressionSessionMd />
      <DeleteProgressionSessionMd />
      <UpdateFileSubmissionSessionMd classId={parseInt(id, 10)} />
      <UpdateQuizSessionMd classId={parseInt(id, 10)} />

      <UpdateClassMd />
      <DeleteClassMd />
      <Flex width="100%" direction="column" p={10} gap={4}>
        <Breadcrumbs
          mb={4}
          links={[
            { name: 'Classes', path: '/admin/classes' },
            {
              name: classObject?.name || 'Loading...',
              path: `/admin/classes/${id}`,
            },
          ]}
        />

        {getClass.status === ReducerStatus.LOADING || !classObject ? (
          <ClassItemLoading minH={200} w="100%" />
        ) : (
          <ScaleFade
            initialScale={0.9}
            in={!getClass.status !== ReducerStatus.LOADING}
          >
            <ClassItem
              minH={200}
              w="100%"
              classObject={classObject}
              isEditModeChecked
              publishStatus={publishStatus}
            />
          </ScaleFade>
        )}

        {getClass.status !== ReducerStatus.LOADING &&
          classObject?.courses.length > 0 && (
            <Text mt={5} mb={3} fontWeight={500}>
              Courses:
            </Text>
          )}

        {getClass.status === ReducerStatus.LOADING || !classObject ? (
          <Grid templateColumns={columnLayout} gap={8}>
            {[...Array(12)].map(() => (
              <GridItem key={uuidv4()} w="100%">
                <CourseItemLoading />
              </GridItem>
            ))}
          </Grid>
        ) : (
          <ScaleFade
            initialScale={0.9}
            in={getClass.status !== ReducerStatus.LOADING && classObject}
          >
            <VStack align="stretch">
              <Grid templateColumns={columnLayout} gap={8}>
                {classObject.courses.map((course) => (
                  <GridItem w="100%" key={course.id}>
                    <CourseItem
                      id={course.id}
                      image={course.thumbnail}
                      category={course.category}
                      name={course.name}
                      description={course.description}
                      isPublished={course.isPublished}
                      isPrivate={course.isPrivate}
                      onClick={() => handleCourseItemClick(course.id)}
                      isAdminMode
                    />
                  </GridItem>
                ))}
              </Grid>
            </VStack>
          </ScaleFade>
        )}

        {getClass.status === ReducerStatus.SUCCEEDED && (
          <ClassRecord mt={5} id={classObject.id} />
        )}
      </Flex>
    </Flex>
  );
}
export default ClassAdminPage;
